import type { MutableRefObject, ReactNode } from 'react';
import React, { createRef } from 'react';
import type {
  FieldValues,
  SubmitHandler,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { useForm, useFormContext } from 'react-hook-form';

import { createElement } from './utils/elements';
import useFormFocus from './utils/useFormFocus';
import { FormValidation } from '@data/other/validation';

export type FormProps<T extends FieldValues> = {
  defaultValues?: UseFormProps<T>['defaultValues'];
  children: ReactNode;
  allFieldsDisabled?: boolean;
  validation?: FormValidation<T>;
  onSubmit?: SubmitHandler<T>;
  formRef?: MutableRefObject<UseFormReturn<T> | null>;
  autoFocus?: boolean;
};

export const Form = <T extends FieldValues>({
  defaultValues,
  children,
  validation = {},
  formRef = createRef<UseFormReturn<T>>(),
  onSubmit,
  allFieldsDisabled = false,
  autoFocus = true,
}: FormProps<T>) => {
  let methods = useForm<T>({ defaultValues });

  const contextMethods = useFormContext<T>();

  if (contextMethods) {
    methods = contextMethods;
  }

  // eslint-disable-next-line no-param-reassign
  formRef.current = methods;

  useFormFocus(methods, autoFocus);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <form onSubmitCapture={methods.handleSubmit(onSubmit || (() => {}))}>
      {createElement(children, methods, validation, allFieldsDisabled)}
    </form>
  );
};

export default Form;
